import React from 'react';
import { ClipboardList } from 'lucide-react';
import type {
  ScoutingReportConfidence,
  ScoutingReportV2,
  ScoutingReportV2Section,
} from '../../lib/reportApi';

export interface CoachReportSectionProps {
  report: ScoutingReportV2;
}

const CONFIDENCE_LABEL: Record<ScoutingReportConfidence, string> = {
  low: 'Low confidence',
  moderate: 'Moderate confidence',
  high: 'High confidence',
};

const CONFIDENCE_CLASS: Record<ScoutingReportConfidence, string> = {
  low: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200',
  moderate:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  high: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
};

function BulletBlock({
  label,
  items,
  testId,
}: {
  label: string;
  items: string[];
  testId: string;
}): React.JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400">
        {label}
      </p>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-secondary-700 dark:text-secondary-300">
        {items.map((item, index) => (
          <li key={`${testId}-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function CoachReportCard({
  section,
}: {
  section: ScoutingReportV2Section;
}): React.JSX.Element {
  const hasBody =
    section.what_we_saw.length > 0 ||
    section.why_it_matters.length > 0 ||
    section.how_to_attack.length > 0;

  return (
    <article
      className="rounded-lg border border-secondary-200 p-4 dark:border-secondary-700"
      data-testid={`coach-report-section-${section.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-base font-semibold text-secondary-900 dark:text-white">
          {section.title}
        </h4>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CONFIDENCE_CLASS[section.confidence]}`}
          data-testid={`coach-report-confidence-${section.key}`}
        >
          {CONFIDENCE_LABEL[section.confidence]}
        </span>
      </div>

      <BulletBlock
        label="What we saw"
        items={section.what_we_saw}
        testId={`coach-report-what-${section.key}`}
      />
      <BulletBlock
        label="Why it matters"
        items={section.why_it_matters}
        testId={`coach-report-why-${section.key}`}
      />
      <BulletBlock
        label="How to attack / adjust"
        items={section.how_to_attack}
        testId={`coach-report-how-${section.key}`}
      />

      {!hasBody && !section.sample_note ? (
        <p className="mt-3 text-sm text-secondary-500 dark:text-secondary-400">
          Not enough film in this sample to detail this area.
        </p>
      ) : null}

      {section.sample_note ? (
        <p
          className="mt-3 text-xs italic text-secondary-500 dark:text-secondary-400"
          data-testid={`coach-report-sample-note-${section.key}`}
        >
          {section.sample_note}
        </p>
      ) : null}
    </article>
  );
}

export const CoachReportSection: React.FC<CoachReportSectionProps> = ({ report }) => {
  return (
    <section className="card" data-testid="coach-report-section">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white">
          Coach Report
        </h3>
      </div>
      <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-300">
        AI-assisted coaching breakdown built from this film sample. Directional scouting
        guidance — validate key tendencies against the evidence timestamps.
      </p>

      <div className="mt-4 space-y-4">
        {report.sections.map((section) => (
          <CoachReportCard key={section.key} section={section} />
        ))}
      </div>
    </section>
  );
};

export default CoachReportSection;
